import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { lockInput, unlockInput } from './inputLock.js';
import { audioManager } from '../audio/AudioManager.js';
import { gamePanel, gameButton } from './gameUi.js';
import { FONT } from './gameUiStyle.mjs';
import { iconText } from './iconText.js';
import { tokenize } from './icons.mjs';
import { enlargeForTouch } from './touchEnlarge.js';

// Modals are positioned at the camera's current view centre in WORLD coordinates.
// This keeps them centred on screen in scrolling scenes (the street) while leaving
// input hit-testing correct — unlike pinning a container with scrollFactor(0), which
// renders pinned but still hit-tests children at the scrolled world position.
function viewCentre(scene) {
  const cam = scene.cameras.main;
  return { ox: cam.scrollX, oy: cam.scrollY, x: cam.scrollX + GAME_WIDTH / 2, y: cam.scrollY + GAME_HEIGHT / 2 };
}

/**
 * Show a modal with a title, body, and two buttons. Returns a Promise<boolean>:
 *   true  = user clicked confirm
 *   false = user clicked cancel
 */
export function showModal(scene, { title, body, confirmLabel = 'OK', cancelLabel = 'Anuluj' }) {
  return new Promise((resolve) => {
    const W = 540, H = 280;
    const { ox, oy, x, y } = viewCentre(scene);
    const layer = scene.add.container(0, 0).setDepth(2000);
    lockInput(scene);

    const overlay = scene.add.rectangle(ox, oy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0).setInteractive();
    // The game look (gameUi.js): a rounded panel, chunky 3D buttons.
    const box = gamePanel(scene, x, y, W, H);
    // Titles, bodies and labels may carry {icon} tokens; legacy emoji strings are tokenized.
    const titleText = iconText(scene, x, y - H/2 + 30, tokenize(String(title ?? '')), {
      fontFamily: FONT, fontSize: '26px', color: '#5a3414', fontStyle: 'bold', align: 'center', wordWrap: { width: W - 50 },
    }).setOrigin(0.5, 0);
    const bodyText = iconText(scene, x, y - 5, tokenize(String(body ?? '')), {
      fontFamily: FONT, fontSize: '20px', color: '#3a2a1a', align: 'center',
      wordWrap: { width: W - 40 },
    }).setOrigin(0.5, 0.5);

    const btnY = y + H/2 - 52;
    let closed = false;
    const close = (result) => { if (closed) return; closed = true; unlockInput(scene); layer.destroy(); resolve(result); };
    // cancelLabel: null → a message with one button (nothing to cancel), centred.
    const cancelBtn = cancelLabel == null ? null
      : gameButton(scene, x - 105, btnY, 170, 52, tokenize(cancelLabel), () => close(false), { variant: 'secondary', fontSize: 20 });
    const confirmBtn = gameButton(scene, cancelBtn ? x + 105 : x, btnY, 170, 52, tokenize(confirmLabel), () => close(true), { variant: 'primary', fontSize: 20 });

    layer.add([overlay, box, titleText, bodyText, ...(cancelBtn ? [cancelBtn] : []), confirmBtn]);
    enlargeForTouch(scene, layer, { x, y, w: W, h: H }, { overlay });   // phone-sized buttons
    overlay.on('pointerup', () => { audioManager.playSfx('click'); close(false); });
  });
}

/**
 * Picker showing a horizontal row of image cards (e.g. seed types). Clicking a card
 * resolves with that item's id; the bottom "Zamknij" button / X / overlay resolve null.
 * items = [{ id, label, count }]; the icon texture is resolved seed_<id> → crop_<id> → plot_empty.
 */
export function showImagePickerModal(scene, { title = 'Wybierz', items = [] }) {
  return new Promise((resolve) => {
    const N = items.length;
    const CELL = 130;
    const ICON = 76;
    const W = Math.max(440, N * CELL + 60);
    const H = 250;
    const { ox, oy, x, y } = viewCentre(scene);
    const layer = scene.add.container(0, 0).setDepth(2000);
    lockInput(scene);

    const overlay = scene.add.rectangle(ox, oy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0).setInteractive();
    const box = scene.add.rectangle(x, y, W, H, 0xffffff, 1).setStrokeStyle(4, 0x333333);
    const titleText = iconText(scene, x, y - H / 2 + 22, tokenize(String(title ?? '')), {
      fontFamily: 'system-ui', fontSize: '26px', color: '#000', fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    layer.add([overlay, box, titleText]);
    enlargeForTouch(scene, layer, { x, y, w: W, h: H }, { overlay });

    const close = (result) => { audioManager.playSfx('click'); unlockInput(scene); layer.destroy(); resolve(result); };

    const closeBtn = scene.add.rectangle(x + W / 2 - 24, y - H / 2 + 24, 36, 36, 0xaa3333, 1)
      .setStrokeStyle(2, 0x000000).setInteractive({ useHandCursor: true });
    const closeTxt = scene.add.text(x + W / 2 - 24, y - H / 2 + 24, '×', {
      fontFamily: 'system-ui', fontSize: '28px', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5);
    layer.add([closeBtn, closeTxt]);
    closeBtn.on('pointerup', () => close(null));
    overlay.on('pointerup', () => close(null));

    // Row of clickable image cards, horizontally centred.
    const rowY = y + 6;
    const startX = x - ((N - 1) * CELL) / 2;
    items.forEach((it, i) => {
      const cxi = startX + i * CELL;
      const tex = scene.textures.exists(`seed_${it.id}`) ? `seed_${it.id}`
        : scene.textures.exists(`crop_${it.id}`) ? `crop_${it.id}` : 'plot_empty';
      const cell = scene.add.rectangle(cxi, rowY, CELL - 14, 150, 0xf3f3f3, 1)
        .setStrokeStyle(2, 0x999999).setInteractive({ useHandCursor: true });
      const icon = scene.add.image(cxi, rowY - 30, tex).setDisplaySize(ICON, ICON);
      const name = iconText(scene, cxi, rowY + 26, tokenize(String(it.label ?? it.id)), {
        fontFamily: 'system-ui', fontSize: '15px', color: '#000', fontStyle: 'bold',
        align: 'center', wordWrap: { width: CELL - 22 },
      }).setOrigin(0.5, 0);
      const have = scene.add.text(cxi, rowY + 50, `masz: ${it.count ?? 0}`, {
        fontFamily: 'system-ui', fontSize: '13px', color: '#555',
      }).setOrigin(0.5, 0);
      layer.add([cell, icon, name, have]);
      cell.on('pointerover', () => cell.setFillStyle(0xffe9a8));
      cell.on('pointerout', () => cell.setFillStyle(0xf3f3f3));
      cell.on('pointerup', () => close(it.id));
    });
    // Closing is via the × (top-right) or tapping outside — no separate button needed.
  });
}

/**
 * Modal z N przyciskami akcji (jeden pod drugim) + X w prawym górnym do zamknięcia.
 * actions = [{ label, fn?: async () => any, disabled?: bool }]
 * Po kliku przycisku: wywołuje fn() i zamyka modal. Przyciski disabled są widoczne,
 * ale wyszarzone i nieklikalne. X / overlay → zamyka bez akcji.
 */
export function showActionsModal(scene, { title, body, actions = [] }) {
  return new Promise((resolve) => {
    const N = actions.length;
    const W = 560;
    const BTN_W = W - 90, BTN_H = 50, GAP = 14;
    const BODY_TOP = 66, MIN_BODY_H = 44, BODY_GAP = 12;
    const { ox, oy, x, y } = viewCentre(scene);
    const layer = scene.add.container(0, 0).setDepth(2000);
    lockInput(scene);

    // The body is laid out first so the box can grow around it: a one-line "Co robimy?" and
    // a whole info block (the garage panel) both have to fit above the buttons.
    const bodyText = iconText(scene, 0, 0, tokenize(String(body ?? '')), {
      fontFamily: 'system-ui', fontSize: '18px', color: '#222', align: 'center',
      wordWrap: { width: W - 40 },
    }).setOrigin(0.5, 0);
    const bodyH = Math.max(MIN_BODY_H, bodyText.height);
    // BODY_TOP (title band) + body + a breathing gap + the button stack + 40 bottom padding.
    const H = BODY_TOP + bodyH + BODY_GAP + 40 + N * (BTN_H + GAP);

    const overlay = scene.add.rectangle(ox, oy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0).setInteractive();
    const box = scene.add.rectangle(x, y, W, H, 0xffffff, 1).setStrokeStyle(4, 0x333333);
    const titleText = iconText(scene, x, y - H/2 + 22, tokenize(String(title ?? '')), {
      fontFamily: 'system-ui', fontSize: '26px', color: '#000', fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    bodyText.setPosition(x, y - H/2 + BODY_TOP);

    layer.add([overlay, box, titleText, bodyText]);
    enlargeForTouch(scene, layer, { x, y, w: W, h: H }, { overlay });

    // X w prawym górnym
    const closeBtn = scene.add.rectangle(x + W/2 - 24, y - H/2 + 24, 36, 36, 0xaa3333, 1)
      .setStrokeStyle(2, 0x000000).setInteractive({ useHandCursor: true });
    const closeTxt = scene.add.text(x + W/2 - 24, y - H/2 + 24, '×', {
      fontFamily: 'system-ui', fontSize: '28px', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5);
    layer.add([closeBtn, closeTxt]);

    const close = (result) => { audioManager.playSfx('click'); unlockInput(scene); layer.destroy(); resolve(result); };
    closeBtn.on('pointerup', () => close(null));
    overlay.on('pointerup', () => close(null));

    // Przyciski akcji — jeden pod drugim, szerokie.
    const firstY = y - H/2 + BODY_TOP + bodyH + BODY_GAP + BTN_H / 2;
    actions.forEach((act, i) => {
      const by = firstY + i * (BTN_H + GAP);
      const btn = scene.add.rectangle(x, by, BTN_W, BTN_H, act.disabled ? 0xcccccc : 0xffd166)
        .setStrokeStyle(2, 0x333333);
      const txt = iconText(scene, x, by, tokenize(String(act.label ?? '')), {
        fontFamily: 'system-ui', fontSize: '20px', color: act.disabled ? '#888' : '#000', fontStyle: 'bold',
      }).setOrigin(0.5);
      layer.add([btn, txt]);
      if (act.disabled) return;
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerup', async () => {
        btn.disableInteractive();
        try {
          const r = act.fn ? await act.fn() : null;
          close(r);
        } catch (e) {
          close({ error: e });
        }
      });
    });
  });
}
